/****************
 * POSTHOG, SHARED BY THE WEB AND THE PHONE SITE. 2026-10-22.
 * TWIN FILE: frontend/src/lib/posthog.core.ts and
 * mobile-app/src/lib/posthog.core.ts must stay byte-identical.
 *
 * Guillaume, 2026-10-21: "PostHog type analytics for our user's behaviour to
 * have the deepest understanding of how they act in our platform." Replays,
 * heatmaps (autocapture), funnels and feature flags, on top of our own events
 * (analytics.ts), which are forwarded under the same names so a PostHog funnel
 * and the admin page count the same things.
 *
 * What it never sees:
 *   - money: every text with a figure in it ($, %, USDT, 1,234.56) is masked
 *     in replays, digits turned to dots. Labels and names stay readable, so a
 *     replay still shows where someone hesitated, never what they hold.
 *   - typed text: every input is masked.
 *   - who they are beyond the account id: no email, no name.
 * Consent: the Terms accepted at sign-up (Guillaume 2026-10-22), and an
 * opt-out switch in Settings that stops it on that device.
 *
 * It must never break the app: the key comes from the server at runtime, and
 * no key, a failed fetch or a failed import means nothing is loaded.
 ****************/

export interface PostHogConfig {
  key: string;
  host: string;
}

/** The device's choice to stop PostHog (Settings). */
export const POSTHOG_OPT_OUT_KEY = `copyfi.analytics.optOut`;

const FIGURE = /[$€£¥%]|\bUSDT?\b|\d[.,]\d|\d{3,}/;

/** A replay shows the layout, never an amount: any text with a figure in it
 *  has its digits turned to dots. */
export function maskFigures(text: string): string {
  if (!text || !/\d/.test(text) || !FIGURE.test(text)) return text;
  return text.replace(/\d/g, '•');
}

/** The options both apps pass to posthog.init. */
export function posthogOptions(cfg: PostHogConfig) {
  return {
    api_host: cfg.host,
    person_profiles: 'identified_only' as const,
    capture_pageview: 'history_change' as const,
    capture_pageleave: true,
    autocapture: true,
    respect_dnt: true,
    disable_surveys: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskTextFn: (text: string) => maskFigures(text),
    },
  };
}

/** Reads the config the server serves; null when PostHog is off. */
export async function fetchPostHogConfig(apiBase: string): Promise<PostHogConfig | null> {
  try {
    const res = await fetch(`${apiBase}/api/v1/config/analytics`);
    if (!res.ok) return null;
    const body = await res.json();
    const ph = body?.posthog;
    if (ph && typeof ph.key === 'string' && typeof ph.host === 'string' && ph.key && ph.host) {
      return { key: ph.key, host: ph.host };
    }
  } catch {
    /* analytics never surfaces an error */
  }
  return null;
}
